import React from 'react';
import { Info, Lightbulb, AlertCircle } from 'lucide-react';
import '../../styles/design-system.css';

/**
 * NoteCard Component
 * Beautiful card-style note component with side accent bar
 * 
 * @param {Object} props
 * @param {string} props.variant - 'info' | 'tip' | 'warning'
 * @param {React.ReactNode} props.children - Note content
 * @param {string} props.className - Additional CSS classes
 */
const NoteCard = ({
    variant = 'info',
    children,
    className = '',
    ...props
}) => {
    const icons = {
        info: Info,
        tip: Lightbulb,
        warning: AlertCircle
    };

    const colorSchemes = {
        info: {
            accent: 'var(--color-info)',
            background: 'var(--color-info-subtle)',
            border: 'var(--color-info-light)',
            text: 'var(--color-text-primary)',
            iconBg: 'var(--color-info)'
        },
        tip: {
            accent: 'var(--color-success)',
            background: 'var(--color-success-subtle)',
            border: 'var(--color-success-light)',
            text: 'var(--color-text-primary)',
            iconBg: 'var(--color-success)'
        },
        warning: {
            accent: 'var(--color-warning)',
            background: 'var(--color-warning-subtle)',
            border: 'var(--color-warning-light)',
            text: 'var(--color-text-primary)',
            iconBg: 'var(--color-warning)'
        }
    };

    const Icon = icons[variant];
    const colors = colorSchemes[variant];

    const { style, ...restProps } = props;
    
    return (
        <div
            className={`note-card ${className}`}
            style={{
                position: 'relative',
                display: 'flex',
                gap: 'var(--spacing-4)',
                padding: 'var(--spacing-5)',
                backgroundColor: colors.background,
                border: `1px solid ${colors.border}`,
                borderLeft: `4px solid ${colors.accent}`,
                borderRadius: 'var(--radius-xl)',
                boxShadow: 'var(--shadow-sm)',
                transition: 'all var(--transition-base)',
                marginBottom: 'var(--spacing-6)',
                ...style
            }}
            {...restProps}
        >
            {/* Side Icon */}
            <div
                style={{
                    flexShrink: 0,
                    width: '40px',
                    height: '40px',
                    borderRadius: 'var(--radius-lg)',
                    backgroundColor: colors.iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 'var(--shadow-sm)'
                }}
            >
                <Icon size={20} color="white" />
            </div>

            {/* Content */}
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-1)'
                }}
            >
                <div
                    style={{
                        fontSize: 'var(--text-sm)',
                        lineHeight: 'var(--leading-relaxed)',
                        color: colors.text
                    }}
                >
                    {children}
                </div>
            </div>
        </div>
    );
};

export default NoteCard;

